"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import { Image } from "@/lib/client/components/Image";
import { Avatar, Box, Button, cn, Grid, Section, Text, VStack } from "@/ui";
import type { Testimonial } from "../../shared/types";
import { SectionBG } from "./SectionBG";

interface TestimonialsProps {
  testimonials: Testimonial[];
}

/**
 * Testimonials component displays customer testimonials
 */
export const Testimonials = ({ testimonials }: TestimonialsProps) => {
  const testimonialsT = useTranslations("testimonials");
  const [isShowingMore, setIsShowingMore] = useState(false);

  const onButtonClick = () => {
    if (isShowingMore) {
      // Scroll to testimonials section
      const element = document.getElementById("testimonials");
      element?.scrollIntoView({ behavior: "smooth" });
    }
    setIsShowingMore(!isShowingMore);
  };

  return (
    <Section
      id="testimonials"
      className="relative isolate"
    >
      <SectionBG />
      <Box className="mx-auto max-w-7xl px-6 lg:px-8">
        <Box className="mx-auto max-w-xl text-center">
          <Text className="text-lg font-semibold leading-8 tracking-tight text-primary">
            {testimonialsT("title")}
          </Text>
          <Text className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
            {testimonialsT("subtitle")}
          </Text>
        </Box>
        <Box
          className={cn(
            isShowingMore ? "" : "max-h-[33rem] overflow-hidden",
            "mx-auto mt-16 flow-root max-w-2xl sm:mt-20 lg:mx-0 lg:max-w-none",
          )}
        >
          <Grid className="-mt-8 sm:-mx-4 sm:grid-cols-2 sm:text-[0] lg:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <Box
                key={testimonial.id || index}
                className="pt-8 sm:inline-block sm:w-full sm:px-4"
              >
                <figure className="rounded-2xl p-8 text-sm leading-6 shadow-lg">
                  <Text>{`"${testimonial.body}"`}</Text>

                  <figcaption className="mt-6 flex items-center gap-x-4">
                    <Avatar className="h-12 w-12 flex-none">
                      <Image
                        src={testimonial.author.headerImage}
                        alt={testimonial.author.name}
                        width={50}
                        height={50}
                      />
                    </Avatar>
                    <VStack>
                      <Text className="font-semibold">{testimonial.author.name}</Text>
                    </VStack>
                  </figcaption>
                </figure>
              </Box>
            ))}
          </Grid>
        </Box>
        <Box
          className={cn(
            isShowingMore
              ? "sticky inset-x-0 bottom-0 flex justify-center rounded-2xl bg-gradient-to-t from-base-100 pb-32 pt-8"
              : "absolute inset-x-0 bottom-0 flex justify-center rounded-2xl bg-gradient-to-t from-base-100 pb-32 pt-8",
          )}
        >
          <Button onClick={onButtonClick}>
            {isShowingMore ? testimonialsT("okay") : testimonialsT("show_more")}
          </Button>
        </Box>
      </Box>
    </Section>
  );
};
